/**
 * AdminFranchisesPanel — CRUD interface for Franchises / Studios / Streaming.
 * Embedded in the existing CinemaContent admin area.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, X, RefreshCw, Clapperboard, Building2, Tv, Sparkles, Upload, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { id: 'franchise', label: 'Franchise', icon: Sparkles },
  { id: 'studio', label: 'Studio', icon: Building2 },
  { id: 'streaming', label: 'Streaming', icon: Tv },
];

const blank = { type: 'franchise', title: '', description: '', image_url: '', external_url: '', price_label: '', is_free: false, country_code: '', sort_order: 0, is_active: true, translations: {} };

const AdminFranchisesPanel = () => {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterType ? `/api/admin/franchises?type=${filterType}` : '/api/admin/franchises';
      const r = await axios.get(url);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const notifyChanged = () => window.dispatchEvent(new CustomEvent('franchises:changed'));

  const openCreate = () => { setForm(blank); setEditing('new'); };
  const openEdit = (it) => {
    setForm({ ...blank, ...it, translations: it.translations || {} });
    setEditing(it.id);
  };
  const close = () => { setEditing(null); setForm(blank); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 MB)');
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post('/api/admin/franchises/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, image_url: r.data.url }));
      toast.success('Image importée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post('/api/admin/franchises', form);
        toast.success('Créé');
      } else {
        await axios.put(`/api/admin/franchises/${editing}`, form);
        toast.success('Mis à jour');
      }
      close();
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Supprimer « ${title} » ?`)) return;
    try {
      await axios.delete(`/api/admin/franchises/${id}`);
      toast.success('Supprimé');
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const seed = async () => {
    if (!window.confirm("Insérer un jeu de données par défaut (uniquement si la liste est vide) ?")) return;
    try {
      const r = await axios.post('/api/admin/franchises/seed');
      toast.success(`${r.data?.seeded || 0} éléments ajoutés`);
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="admin-franchises-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Clapperboard className="w-5 h-5 text-purple-300" />
          <h3 className="font-bold text-white">Franchises, Studios & Streaming</h3>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-xs text-white"
            data-testid="admin-franchises-filter"
          >
            <option value="">Tous types</option>
            {TYPE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={seed} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs text-white flex items-center gap-1.5" data-testid="admin-franchises-seed">
            <RefreshCw className="w-3.5 h-3.5" /> Seed défaut
          </button>
          <button onClick={openCreate} className="px-3 py-1.5 rounded-lg bg-purple-500 hover:bg-purple-600 text-xs text-white font-semibold flex items-center gap-1.5" data-testid="admin-franchises-create">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-white/40 text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm" data-testid="admin-franchises-empty">Aucun élément. Cliquez « Seed défaut » pour démarrer.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-franchises-list">
          {items.map(it => (
            <div key={it.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex gap-3" data-testid={`admin-franchise-${it.id}`}>
              <div className="w-14 h-14 bg-zinc-900 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {it.image_url ? <img src={it.image_url} alt={it.title} className="max-w-full max-h-full object-contain" /> : <Tv className="w-5 h-5 text-white/30" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-white/40">{it.type}</div>
                    <div className="text-white font-semibold truncate">{it.title}</div>
                    {it.price_label && <div className="text-[11px] text-emerald-300">{it.price_label}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(it)} className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white" data-testid={`admin-franchise-edit-${it.id}`}><Edit className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(it.id, it.title)} className="p-1.5 rounded bg-red-500/15 hover:bg-red-500/25 text-red-300" data-testid={`admin-franchise-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {it.external_url && <a href={it.external_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-purple-300 truncate block mt-1">{it.external_url}</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-franchises-modal">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">{editing === 'new' ? 'Nouvel élément' : 'Modifier'}</h4>
              <button onClick={close} className="p-1.5 rounded hover:bg-white/10 text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-white/60 mb-1">Type *</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="admin-franchise-form-type">
                  {TYPE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <Field label="Titre *" value={form.title} onChange={v => setForm({ ...form, title: v })} testid="admin-franchise-form-title" />
              <Field label="Description" value={form.description || ''} onChange={v => setForm({ ...form, description: v })} multiline />

              {/* Image: upload OR URL */}
              <div>
                <label className="block text-xs text-white/60 mb-1">Image / Logo</label>
                {form.image_url && (
                  <div className="mb-2 flex items-center gap-2">
                    <div className="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                      <img src={form.image_url} alt="preview" className="max-w-full max-h-full object-contain" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: '' })}
                      className="text-xs text-red-300 hover:text-red-200"
                      data-testid="admin-franchise-form-clear-image"
                    >
                      Retirer
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex-1 px-3 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-200 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="admin-franchise-form-upload"
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingImage ? 'Envoi…' : 'Importer une image'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <input
                  type="text"
                  value={form.image_url || ''}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="… ou coller une URL d'image"
                  className="w-full mt-2 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="admin-franchise-form-image-url"
                />
                <p className="text-[10px] text-white/40 mt-1">Formats: JPG, PNG, WebP, GIF, SVG · max 10 MB</p>
              </div>

              <Field label="URL externe (site ou Wikipédia)" value={form.external_url || ''} onChange={v => setForm({ ...form, external_url: v })} />
              <Field label="Prix (ex: 9,99 €/mois)" value={form.price_label || ''} onChange={v => setForm({ ...form, price_label: v })} />
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={form.is_free} onChange={(e) => setForm({ ...form, is_free: e.target.checked })} data-testid="admin-franchise-form-free" />
                  Gratuit
                  <span title="Cochez si ce service/franchise est gratuit — un badge 'Gratuit' vert s'affichera dans l'app."><HelpCircle className="w-3.5 h-3.5 text-white/40" /></span>
                </label>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="admin-franchise-form-active" />
                  Visible dans l&apos;app
                  <span title="Si décoché, cet élément reste dans la base mais est masqué de la section publique 'Franchises, Studios &amp; Streaming'."><HelpCircle className="w-3.5 h-3.5 text-white/40" /></span>
                </label>
              </div>
              <Field label="Pays (ex: FR)" value={form.country_code || ''} onChange={v => setForm({ ...form, country_code: v.toUpperCase() })} />
              <Field label="Ordre d'affichage" type="number" value={form.sort_order ?? 0} onChange={v => setForm({ ...form, sort_order: parseInt(v) || 0 })} />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm text-white">Annuler</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-sm text-white font-semibold disabled:opacity-50" data-testid="admin-franchise-form-save">
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, multiline = false, type = 'text', testid }) => (
  <div>
    <label className="block text-xs text-white/60 mb-1">{label}</label>
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid={testid} />
    )}
  </div>
);

export default AdminFranchisesPanel;
